/**
 * Transcript view component
 */

import {useEffect, useState, useMemo} from 'react';
import {useTranscriptionStore} from '@voxapilot/shared';
import {api} from '../../config/firebase';
import {Loading} from '../ui/Loading';
import {EmptyState} from '../ui/EmptyState';
import {SpeakerLabels, SingleSpeakerBadge} from './SpeakerLabels';
import {cn} from '../../utils/cn';

import type {AudioPlayerRef} from '../audio/AudioPlayer';

interface TranscriptViewProps {
  meetingId: string;
  audioPlayerRef?: React.RefObject<AudioPlayerRef | null>;
}

export function TranscriptView({meetingId, audioPlayerRef}: TranscriptViewProps) {
  const {getTranscript, setTranscript, setLoading, setError} = useTranscriptionStore();
  const [isLoading, setIsLoading] = useState(true);
  const [speakerNames, setSpeakerNames] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const transcript = getTranscript(meetingId);

  // Extract unique speakers from transcript segments
  const uniqueSpeakers = useMemo(() => {
    if (!transcript?.segments) return [];
    const speakers = new Set<string>();
    transcript.segments.forEach((segment) => {
      if (segment.speaker) {
        speakers.add(segment.speaker);
      }
    });
    return Array.from(speakers).sort();
  }, [transcript]);

  // Handle speaker name change (for now, just update local state)
  // TODO: In future, this should save to backend
  const handleSpeakerNameChange = (label: string, displayName: string) => {
    setSpeakerNames((prev) => ({
      ...prev,
      [label]: displayName,
    }));
  };

  // Handle segment click - seek audio to segment start time
  const handleSegmentClick = async (startTime: number) => {
    if (audioPlayerRef?.current) {
      audioPlayerRef.current.seekTo(startTime);
      await audioPlayerRef.current.play();
    }
  };

  // Filter segments by search query
  const filteredSegments = useMemo(() => {
    if (!transcript?.segments || !searchQuery.trim()) {
      return transcript?.segments || [];
    }

    const query = searchQuery.toLowerCase();
    return transcript.segments.filter((segment) => {
      const textMatch = segment.text.toLowerCase().includes(query);
      const speakerMatch = segment.speaker?.toLowerCase().includes(query);
      return textMatch || speakerMatch;
    });
  }, [transcript?.segments, searchQuery]);

  // Highlight search matches in text
  const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) {
      return text;
    }

    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, index) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        return (
          <mark
            key={index}
            className="bg-yellow-200 dark:bg-yellow-900/50 text-gray-900 dark:text-white px-0.5 rounded"
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  useEffect(() => {
    loadTranscript();
  }, [meetingId]);

  const loadTranscript = async () => {
    try {
      setIsLoading(true);
      setLoading(meetingId, true);
      setError(meetingId, null);

      const response = await api.transcription.getTranscript(meetingId);
      if (response.success && response.data) {
        setTranscript(meetingId, response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to load transcript');
      }
    } catch (err) {
      const error = err as Error;
      setError(meetingId, error.message);
    } finally {
      setIsLoading(false);
      setLoading(meetingId, false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loading size="md" />
      </div>
    );
  }

  if (!transcript) {
    return (
      <EmptyState
        icon="📄"
        title="No transcript available"
        description="The transcript is being processed. Please check back in a few moments."
        size="sm"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
          <span>Language: {transcript.language}</span>
          <span>Duration: {Math.round(transcript.duration)}s</span>
          {uniqueSpeakers.length > 0 && (
            <span>{uniqueSpeakers.length} {uniqueSpeakers.length === 1 ? 'Speaker' : 'Speakers'}</span>
          )}
        </div>

        {/* Speaker Labels Section */}
        {uniqueSpeakers.length > 0 && (
          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Speakers
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Click to edit names
              </span>
            </div>
            <SpeakerLabels
              speakers={uniqueSpeakers}
              speakerNames={speakerNames}
              onSpeakerNameChange={handleSpeakerNameChange}
              editable={true}
              size="sm"
            />
          </div>
        )}
        
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
          <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
            {transcript.fullText}
          </p>
        </div>
      </div>

      {transcript.segments && transcript.segments.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Segments
            </h3>
            
            {/* Search Input */}
            <div className="flex-1 max-w-md ml-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transcript..."
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {filteredSegments.length === 0 ? (
            <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-gray-600 dark:text-gray-400">
                No segments found matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSegments.map((segment) => (
                <div
                  key={segment.id}
                  className={cn(
                    'group bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700',
                    'transition-all duration-200',
                    audioPlayerRef && 'cursor-pointer hover:border-blue-500 hover:shadow-md'
                  )}
                  onClick={() => audioPlayerRef && handleSegmentClick(segment.start)}
                  title={audioPlayerRef ? 'Click to play from this segment' : undefined}
                >
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                      {formatTime(segment.start)} - {formatTime(segment.end)}
                    </span>
                    {segment.speaker && (
                      <SingleSpeakerBadge
                        speaker={segment.speaker}
                        speakerNames={speakerNames}
                        size="sm"
                      />
                    )}
                    {audioPlayerRef && (
                      <span className="text-xs text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        ▶ Click to play
                      </span>
                    )}
                  </div>
                  <p className="text-gray-900 dark:text-white">
                    {highlightText(segment.text, searchQuery)}
                  </p>
                </div>
              ))}
            </div>
          )}

          {searchQuery && filteredSegments.length > 0 && (
            <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 text-center">
              Showing {filteredSegments.length} of {transcript.segments.length} segments
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

